import type { ReactNode } from 'react';
import { BoxedText } from '@/components/common/BoxedText';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { formatDisplayDate, formatDisplayDateTime } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import type { MpApprovalDetail } from '../types/marketPromotion.types';

const DETAIL_LINE_CLASS = 'block text-xs font-normal text-muted-foreground';
const NO_COMMENT_TEXT = 'No comment';
const NOT_RECOMMENDED_TEXT = 'Not recommended yet';
const NOT_APPROVED_TEXT = 'Not approved yet';

export interface DonationRow {
  label: string;
  value: (donation: MpApprovalDetail) => ReactNode;
}

const formatOptionalNumber = (value: number | null): string | null =>
  value === null ? null : formatNumber(value);

function getNameWithBracketDetails(
  name: string | null,
  details: readonly (string | null)[],
): string | null {
  if (!name) return null;
  const shownDetails = details.filter(Boolean);
  if (shownDetails.length === 0) return name;
  return `${name} (${shownDetails.join(', ')})`;
}

const getTeacherValue = (donation: MpApprovalDetail): string | null =>
  getNameWithBracketDetails(donation.teacherName, [
    donation.teacherDesignation,
    donation.teacherSubject,
  ]);

const getInstituteValue = (donation: MpApprovalDetail): string | null =>
  getNameWithBracketDetails(donation.instituteName, [
    donation.instituteCode === null ? null : String(donation.instituteCode),
  ]);

function getSourceOfFund(donation: MpApprovalDetail): string | null {
  if (donation.sourceName === null || donation.sourceOfFund === null) return donation.sourceName;
  return `${donation.sourceName} (${String(donation.sourceOfFund)})`;
}

function getCommentValue(comment: string | null, missingText: string = NO_COMMENT_TEXT): ReactNode {
  if (comment) return <BoxedText>{comment}</BoxedText>;
  return <BoxedText isMuted>{missingText}</BoxedText>;
}

function getDecisionMissingText(decidedBy: string | null, waitingText: string): string {
  return decidedBy === null ? waitingText : NO_COMMENT_TEXT;
}

export const DONATION_ROWS: DonationRow[] = [
  { label: 'ZM', value: (donation) => donation.enteredByName },
  { label: 'Institute', value: getInstituteValue },
  { label: 'Teacher', value: getTeacherValue },
  {
    label: 'Entry Date',
    value: (donation) => (donation.enteredAt ? formatDisplayDateTime(donation.enteredAt) : null),
  },
  {
    label: 'DSL No',
    value: (donation) => (donation.dslNo === null ? null : String(donation.dslNo)),
  },
];

export const PAYMENT_ROWS: DonationRow[] = [
  { label: 'Source of Fund', value: getSourceOfFund },
  {
    label: 'Amount',
    value: (donation) => `${TAKA_SYMBOL} ${formatNumber(donation.approvedAmount)}`,
  },
  { label: 'Payment Type', value: (donation) => donation.paymentType },
  { label: 'Transaction ID', value: (donation) => donation.transactionRef },
  { label: 'Approx Sale', value: (donation) => formatOptionalNumber(donation.approxSaleQty) },
  { label: 'Payment Place', value: (donation) => donation.paymentPlace },
  { label: 'Witness', value: (donation) => donation.witness },
  { label: 'Total Students', value: (donation) => formatOptionalNumber(donation.totalStudent) },
  {
    label: 'Given Date',
    value: (donation) => (donation.givenDate ? formatDisplayDate(donation.givenDate) : null),
  },
];

export const ZONAL_MANAGER_COMMENT_ROWS: DonationRow[] = [
  { label: 'ZM Comment', value: (donation) => getCommentValue(donation.recordComments) },
  { label: 'ZM Detail Comment', value: (donation) => getCommentValue(donation.detailComment) },
];

export const OWN_INSTITUTE_SOURCE_ROW: DonationRow = {
  label: 'Source of Fund',
  value: (donation) =>
    donation.instituteName === null || donation.instituteCode === null
      ? donation.instituteName
      : `${donation.instituteName} (${String(donation.instituteCode)})`,
};

export const RECOMMEND_COMMENT_ROW: DonationRow = {
  label: 'Recommend Comment',
  value: (donation) => (
    <>
      {getCommentValue(
        donation.recommendedComments,
        getDecisionMissingText(donation.recommendedBy, NOT_RECOMMENDED_TEXT),
      )}
      {donation.recommendedBy ? (
        <span className={DETAIL_LINE_CLASS}>{`By ${donation.recommendedBy}`}</span>
      ) : null}
    </>
  ),
};

export const DECISION_ROWS: DonationRow[] = [
  RECOMMEND_COMMENT_ROW,
  {
    label: 'Approval Comment',
    value: (donation) =>
      getCommentValue(
        donation.approvalComments,
        getDecisionMissingText(donation.approvedBy, NOT_APPROVED_TEXT),
      ),
  },
];
